import { QueryClient } from "@tanstack/react-query";

const userProfileRootKey = ["user", "profile"] as const;
const privateCommunitiesRootKey = ["communities"] as const;
const liveCommunitiesRootKey = [
  ...privateCommunitiesRootKey,
  "public",
] as const;
const campaignsRootKey = ["campaigns"] as const;
const privateCampaignsRootKey = [...campaignsRootKey, "private"] as const;
const liveCampaignsRootKey = [...campaignsRootKey, "public"] as const;
const notificationsRootKey = ["notifications"] as const;
const gmRootKey = ["gm"] as const;
const participantsRootKey = ["community", "participants"] as const;
const leaderboardRootKey = ["leaderboard"] as const;
const campaignMemberActivitiesRootKey = ["campaign", "member-activities"] as const;
const adminSubmissionsRootKey = ["admin", "submissions"] as const;
const referralRootKey = ["referrals"] as const;
const blocksAuditRootKey = ["blocks-audit"] as const;

export const queryKeys = {
  userProfileRoot: userProfileRootKey,
  userProfile: (userId: string) => [...userProfileRootKey, userId] as const,
  publicUserProfile: (userId: string) => ["publicUserProfile", userId] as const,
  privateCommunities: () => [...privateCommunitiesRootKey, "private"] as const,
  theCommunity: (communityId: string) =>
    [...privateCommunitiesRootKey, communityId] as const,
  allLiveCommunities: (params?: { limit?: number; cursor?: string }) =>
    [
      ...liveCommunitiesRootKey,
      params?.limit ?? "default",
      params?.cursor ?? "start",
    ] as const,
  campaigns: (communityId: string) =>
    [...campaignsRootKey, communityId] as const,
  theCampaign: (campaignId: string) =>
    [...campaignsRootKey, campaignId] as const,
  privateCampaigns: (communityId: string) =>
    [...privateCampaignsRootKey, communityId] as const,
  liveCampaigns: (communityId: string) =>
    [...liveCampaignsRootKey, communityId] as const,
  allLiveCampaigns: (communityIds: string[]) =>
    ([...liveCampaignsRootKey, "all", ...communityIds.sort()] as const),
  teamMembers: (communityId: string) =>
    [...privateCommunitiesRootKey, communityId, "teamMembers"] as const,
  teamInvites: (communityId: string) =>
    [...privateCommunitiesRootKey, communityId, "teamInvites"] as const,
  participants: {
    community: (communityId: string) =>
      ([...participantsRootKey, communityId] as const),
    memberProfile: (communityId: string, userId: string) =>
      ([...participantsRootKey, communityId, "member-profile", userId] as const),
    memberActivities: (
      communityId: string,
      userId: string,
      campaignId?: string,
    ) =>
      ([
        ...participantsRootKey,
        communityId,
        "member-activities",
        userId,
        campaignId ?? "all-campaigns",
      ] as const),
  },
  campaignLeaderboard: (campaignId: string, top: number = 100) =>
    [...leaderboardRootKey, "campaign", campaignId, top] as const,
  campaignWinners: (campaignId: string) =>
    [...leaderboardRootKey, "campaign-winners", campaignId] as const,
  communityLeaderboard: (communityId: string, top: number = 100) =>
    [...leaderboardRootKey, "community", communityId, top] as const,
  platformLeaderboard: (seasonId: string = "current", top: number = 100) =>
    [...leaderboardRootKey, "platform", seasonId, top] as const,
  notifications: (userId: string) => [...notificationsRootKey, userId] as const,
  gmStatus: (userId: string) => [...gmRootKey, userId] as const,
  communityNotificationFollow: (userId: string, communityId: string) =>
    [...notificationsRootKey, "follow", userId, communityId] as const,
  myCampaignSubmissions: (userId: string, campaignId: string) =>
    ["campaign-submissions", "mine", userId, campaignId] as const,
  myCampaignSubmissionsLite: (userId: string, campaignId: string) =>
    ["campaign-submissions", "mine-lite", userId, campaignId] as const,
  adminSubmissionPendingSummary: (communityId: string) =>
    [...adminSubmissionsRootKey, "pending-summary", communityId] as const,
  adminOnboardingProgress: (userId: string, communityId: string) =>
    ["admin", "onboarding", userId, communityId] as const,
  campaignMemberActivities: (
    campaignId: string,
    userId: string,
    limit: number = 100,
  ) => [...campaignMemberActivitiesRootKey, campaignId, userId, limit] as const,
  myReferralProgram: (userId: string) => [...referralRootKey, userId] as const,
  myBlocksAudit: (userId: string, limit: number = 3) =>
    [...blocksAuditRootKey, userId, limit] as const,
};

//TODO make it the default for all react-query usage in the app
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 15 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      retry: 1,
    },
  },
});
